// SPDX-License-Identifier: Apache-2.0

// pdf → markdown via `pdfjs-dist`.
//
// Worker URL setup (Vite-compatible):
//   pdfjs-dist v4+ ships its worker as an ESM module at
//   `pdfjs-dist/build/pdf.worker.min.mjs`. Vite's `?url` import suffix returns
//   the bundler-resolved absolute URL of any asset — that's what we hand to
//   `GlobalWorkerOptions.workerSrc` so pdfjs can spawn its own Worker. We do
//   NOT instantiate the Worker ourselves (that path tripped Vite's HMR in the
//   old Tether during dev). The dynamic import of the `?url` specifier means
//   the worker stays a code-split chunk; cold load doesn't pay for it.
//
//   Tested combinations (works in Vite 6 + Bun + tsc):
//     `import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'` — works
//     in Vite, fails under bare tsc (no `?url` resolver). Workaround: dynamic
//     `import(... '?url')` so type-only compile passes.
//
// Per-page joining: pages are separated by `\n\n---\n\n` (HR rule) so the
// markdown viewer can scroll-snap by page. Heading detection: when a page has
// a single line whose font size is >1.4× the median size of that page, we
// promote it to `## Heading`. Best-effort — pdfjs doesn't expose paragraph
// structure, only positioned glyph runs.

import {
  asParagraphs,
  baseTitle,
  corruptResult,
  warnIfLarge,
  wrapWithTitle,
  type IngestResult,
} from './common';

interface PdfTextItem {
  str: string;
  hasEOL?: boolean;
  /** transform[3] = font size when no scale; abs(transform[3]) is robust. */
  transform?: number[];
  height?: number;
}

interface PdfTextContent {
  items: PdfTextItem[];
}

interface PdfPage {
  getTextContent(): Promise<PdfTextContent>;
}

interface PdfDocument {
  numPages: number;
  getPage(n: number): Promise<PdfPage>;
}

interface PdfjsModule {
  GlobalWorkerOptions: { workerSrc?: string; workerPort?: Worker | null };
  getDocument(args: { data: ArrayBuffer }): { promise: Promise<PdfDocument> };
}

let workerConfigured = false;

async function ensureWorker(pdfjs: PdfjsModule): Promise<void> {
  if (workerConfigured) return;
  workerConfigured = true;
  try {
    // The `?url` suffix is a Vite primitive — it returns a string URL pointing
    // at the bundled worker. Wrapped in a runtime variable so tsc doesn't try
    // to resolve it at type-check time.
    const spec = 'pdfjs-dist/build/pdf.worker.min.mjs?url';
    const mod = (await import(/* @vite-ignore */ spec)) as { default?: string };
    if (mod.default) {
      pdfjs.GlobalWorkerOptions.workerSrc = mod.default;
      return;
    }
  } catch {
    // Fall through.
  }
  try {
    // Fallback for non-Vite contexts (Bun tests, raw node) — point at the file
    // path resolved through `import.meta.resolve`-style URL construction.
    const url = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url);
    pdfjs.GlobalWorkerOptions.workerSrc = url.toString();
  } catch {
    // Last resort: leave unset and let pdfjs fall back to its internal worker.
    // Some builds run "fakeWorker" on the main thread when this is empty.
  }
}

export async function parsePdf(file: File): Promise<IngestResult> {
  warnIfLarge(file, 'pdf');
  const title = baseTitle(file.name);
  try {
    const mod = await import('pdfjs-dist');
    const pdfjs = (mod as unknown as PdfjsModule);
    await ensureWorker(pdfjs);

    const data = await file.arrayBuffer();
    const loadingTask = pdfjs.getDocument({ data });
    const pdf = await loadingTask.promise;

    const pageBlocks: string[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const tc = await page.getTextContent();
      pageBlocks.push(renderPage(tc.items));
    }
    const body = pageBlocks
      .map((b) => b.trim())
      .filter(Boolean)
      .join('\n\n---\n\n');

    return {
      title,
      content: wrapWithTitle(title, asParagraphs(body)),
      front: {},
      kind: 'pdf',
    };
  } catch (err) {
    return corruptResult(file.name, err instanceof Error ? err.message : String(err));
  }
}

function fontSize(item: PdfTextItem): number {
  if (item.height && item.height > 0) return item.height;
  const t = item.transform;
  if (Array.isArray(t) && t.length >= 4) {
    const v = t[3];
    if (typeof v === 'number' && v !== 0) return Math.abs(v);
  }
  return 0;
}

function renderPage(items: PdfTextItem[]): string {
  // Group items into lines using `hasEOL` hints; track each line's max font
  // size for the heading heuristic.
  const lines: Array<{ text: string; size: number }> = [];
  let buf: string[] = [];
  let maxSize = 0;
  for (const it of items) {
    if (typeof it.str === 'string') buf.push(it.str);
    const s = fontSize(it);
    if (s > maxSize) maxSize = s;
    if (it.hasEOL) {
      const text = buf.join(' ').replace(/\s+/g, ' ').trim();
      if (text) lines.push({ text, size: maxSize });
      buf = [];
      maxSize = 0;
    }
  }
  if (buf.length > 0) {
    const text = buf.join(' ').replace(/\s+/g, ' ').trim();
    if (text) lines.push({ text, size: maxSize });
  }
  if (lines.length === 0) return '';

  // Median font size on the page (robust against the occasional huge logo).
  const sizes = lines.map((l) => l.size).filter((s) => s > 0).sort((a, b) => a - b);
  const median = sizes.length > 0 ? (sizes[Math.floor(sizes.length / 2)] ?? 0) : 0;

  const out: string[] = [];
  for (const line of lines) {
    if (median > 0 && line.size > median * 1.4 && line.text.length < 120) {
      out.push(`## ${line.text}`);
    } else {
      out.push(line.text);
    }
  }
  return out.join('\n');
}
